import type { AccessTokenProvider } from '../auth/naaAuthProvider';
import type { ClientBuildConfig } from '../config/buildConfig';

export class ApiError extends Error {
  readonly status: number;
  readonly errorCode?: string;
  readonly response: Response;

  constructor(response: Response, message?: string, errorCode?: string) {
    super(message ?? `API request failed with status ${response.status}.`);
    this.name = 'ApiError';
    this.status = response.status;
    this.errorCode = errorCode;
    this.response = response;
  }
}

export type HttpClient = {
  get: <ResponseBody>(path: string, init?: RequestInit) => Promise<ResponseBody>;
  post: <ResponseBody>(path: string, body?: unknown, init?: RequestInit) => Promise<ResponseBody>;
};

type Fetcher = typeof fetch;

function buildUrl(baseUrl: string, path: string): string {
  if (!baseUrl) {
    return path;
  }

  return new URL(path.replace(/^\/+/, ''), `${baseUrl.replace(/\/+$/, '')}/`).toString();
}

async function parseResponse<ResponseBody>(response: Response): Promise<ResponseBody> {
  if (!response.ok) {
    let message: string | undefined;
    let errorCode: string | undefined;
    try {
      const body = (await response.clone().json()) as { message?: unknown; errorCode?: unknown };
      if (typeof body.message === 'string') message = body.message;
      if (typeof body.errorCode === 'string') errorCode = body.errorCode;
    } catch {
      // Keep the status-based fallback for non-JSON error responses.
    }
    throw new ApiError(response, message, errorCode);
  }

  if (response.status === 204) {
    return undefined as ResponseBody;
  }

  return (await response.json()) as ResponseBody;
}

export function createHttpClient(
  config: ClientBuildConfig,
  getAccessToken: AccessTokenProvider,
  fetcher: Fetcher = globalThis.fetch,
): HttpClient {
  async function request<ResponseBody>(
    path: string,
    method: string,
    body: unknown,
    init: RequestInit = {},
  ): Promise<ResponseBody> {
    const accessToken = await getAccessToken();
    const headers = new Headers(init.headers);
    headers.set('Authorization', `Bearer ${accessToken}`);

    if (body !== undefined && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }

    const response = await fetcher(buildUrl(config.apiBaseUrl, path), {
      ...init,
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });

    return parseResponse<ResponseBody>(response);
  }

  return {
    get: <ResponseBody>(path: string, init?: RequestInit) =>
      request<ResponseBody>(path, 'GET', undefined, init),
    post: <ResponseBody>(path: string, body?: unknown, init?: RequestInit) =>
      request<ResponseBody>(path, 'POST', body, init),
  };
}
